import React from "react";
import { LegalLayout, LegalSection } from "@/components/site/LegalLayout";
import { SITE, LEGAL } from "@/lib/site-data";
import { openCookieSettings } from "@/lib/consent";

export default function PrivacyPolicy() {
    return (
        <LegalLayout title="Politique de confidentialité" updated={LEGAL.updated} testId="privacy-page">
            <p className="mt-10 font-sans text-brand-ink/70 leading-relaxed">
                La protection de vos données nous tient à cœur. Cette politique explique quelles données nous traitons
                via ce site, pourquoi et quels sont vos droits, conformément à la loi fédérale sur la protection des
                données (nLPD).
            </p>

            <LegalSection title="Responsable du traitement">
                <p>
                    {LEGAL.company}, {SITE.address.line1}, {SITE.address.zip} {SITE.address.city} — IDE {LEGAL.uid}<br />
                    Contact : <a className="ll-link-underline" href={`mailto:${SITE.email}`}>{SITE.email}</a> · {SITE.phoneDisplay}
                </p>
            </LegalSection>

            <LegalSection title="Prise de rendez-vous en ligne">
                <p>
                    Lorsque vous réservez, nous collectons vos nom, prénom, e-mail, téléphone, date de naissance (pour
                    les vaccinations), la prestation choisie et vos éventuelles remarques. Ces données servent uniquement
                    à organiser et confirmer votre rendez-vous et à vous recontacter si nécessaire.
                </p>
                <p>Elles ne sont accessibles qu'à l'équipe de la pharmacie et ne sont jamais vendues ni cédées à des tiers.</p>
            </LegalSection>

            <LegalSection title="Données de santé">
                <p>
                    La prestation choisie (par exemple une vaccination) et, le cas échéant, vos remarques peuvent révéler
                    des informations sur votre santé, considérées comme des données sensibles par la loi. Nous vous
                    demandons de ne pas inscrire d'informations médicales confidentielles dans le champ « Précisions » :
                    elles seront abordées directement avec le pharmacien.
                </p>
                <p>
                    Ces données sont soumises au secret professionnel du pharmacien. Seuls les membres de l'équipe
                    disposant d'un compte autorisé y accèdent, via une connexion sécurisée. Les échanges avec le site
                    sont chiffrés (HTTPS) et les rendez-vous sont supprimés automatiquement 12 mois après leur date.
                </p>
            </LegalSection>

            <LegalSection title="Formulaire de contact">
                <p>Les informations saisies (nom, e-mail, téléphone, message) sont transmises à la pharmacie par e-mail pour répondre à votre demande.</p>
            </LegalSection>

            <LegalSection title="Mesure d'audience (cookies)">
                <p>
                    Avec votre accord, nous utilisons Google Analytics (Google Ireland Ltd.) pour mesurer la
                    fréquentation du site de façon statistique. Sans votre accord, aucun cookie de mesure n'est déposé.
                    Ces données peuvent être traitées hors de Suisse, notamment aux États-Unis.
                </p>
                <p>
                    Vous pouvez modifier votre choix à tout moment :{" "}
                    <button type="button" onClick={openCookieSettings} className="underline underline-offset-4 text-brand-primary" data-testid="privacy-cookie-settings-btn">
                        gérer les cookies
                    </button>.
                </p>
            </LegalSection>

            <LegalSection title="Carte Google Maps">
                <p>
                    La carte de la section Contact est fournie par Google. Elle ne se charge que si vous acceptez les
                    cookies ou si vous cliquez sur « Afficher la carte » ; Google peut alors collecter des données de
                    navigation (notamment votre adresse IP), possiblement aux États-Unis.
                </p>
            </LegalSection>

            <LegalSection title="Sous-traitants">
                <p>
                    Hébergement du site et de la base de données : {LEGAL.host}. Le trafic du site transite par
                    Cloudflare. Envoi des e-mails et connexion de l'équipe : Infomaniak Network SA (Genève, Suisse).
                    Ces prestataires traitent les données uniquement pour notre compte.
                </p>
            </LegalSection>

            <LegalSection title="Communication de données à l'étranger">
                <p>
                    Les données du site (rendez-vous, messages de contact) sont hébergées par notre prestataire
                    technique sur des serveurs situés aux <strong>États-Unis</strong> et en <strong>Inde</strong>. Les
                    e-mails sont traités en Suisse par Infomaniak. Les statistiques Google Analytics, si vous les
                    acceptez, peuvent être traitées aux États-Unis.
                </p>
                <p>
                    Les États-Unis ne sont reconnus par la Suisse comme offrant un niveau de protection adéquat que pour
                    les entreprises certifiées selon le Swiss-U.S. Data Privacy Framework ; l'Inde ne bénéficie pas d'une
                    telle reconnaissance. Nous sommes en train de vérifier auprès de notre prestataire les garanties
                    applicables (certification ou clauses contractuelles types) et mettrons cette page à jour en
                    conséquence. Pour toute question, contactez-nous.
                </p>
            </LegalSection>

            <LegalSection title="Durée de conservation">
                <p>
                    Les rendez-vous sont conservés 12 mois après leur date et les messages du formulaire de contact
                    12 mois après leur réception, puis supprimés automatiquement.
                </p>
            </LegalSection>

            <LegalSection title="Vos droits">
                <p>Dans les limites prévues par la loi, vous pouvez :</p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>obtenir des informations sur vos données et en recevoir une copie ;</li>
                    <li>faire rectifier des données inexactes ;</li>
                    <li>demander leur effacement ou la limitation de certains traitements ;</li>
                    <li>vous opposer à certains usages, par exemple la mesure d'audience ;</li>
                    <li>lorsque les conditions légales sont réunies, obtenir la remise de vos données ou leur transmission à un tiers.</li>
                </ul>
                <p>
                    Il suffit d'écrire à{" "}
                    <a className="ll-link-underline" href={`mailto:${SITE.email}`}>{SITE.email}</a>. Vous pouvez également
                    vous adresser au Préposé fédéral à la protection des données et à la transparence (PFPDT).
                </p>
            </LegalSection>
        </LegalLayout>
    );
}
